/**
 * Reliability Revenue Impact Section
 * Section 3: Reliability-indexed upsell probability, errors avoided, uptime impact
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface ReliabilityRevenueImpactProps {
  data?: {
    upsellProbability?: Array<{
      reliabilityScore: number;
      probability: number;
    }>;
    errorsAvoided?: {
      total: number;
      revenueAttribution: number;
    };
    uptimeImpact?: {
      churnReduction: number;
    };
    riskRegisterHeatmap?: Array<{
      risk: string;
      revenueImpact: number;
    }>;
  };
}

export function ReliabilityRevenueImpact({ data }: ReliabilityRevenueImpactProps) {
  const formatCurrency = (value?: number) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value?: number) => {
    if (value === undefined) return 'N/A';
    return `${value.toFixed(2)}%`;
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Errors Avoided</CardTitle>
            <CardDescription>Revenue impact from reliability</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Total Errors Avoided</span>
                <span className="font-semibold">{data?.errorsAvoided?.total || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Revenue Attribution</span>
                <span className="font-semibold text-green-600">
                  {formatCurrency(data?.errorsAvoided?.revenueAttribution)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Uptime Impact</CardTitle>
            <CardDescription>Churn reduction from reliability</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatPercent(data?.uptimeImpact?.churnReduction)}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Churn reduction attributed to high uptime
            </p>
          </CardContent>
        </Card>
      </div>

      {data?.upsellProbability && data.upsellProbability.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Upsell Probability by Reliability Score</CardTitle>
            <CardDescription>Higher reliability = higher upsell probability</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.upsellProbability.map((item, index) => (
                <div key={index} className="flex justify-between items-center p-2 border rounded">
                  <span className="text-sm">
                    Reliability Score: <span className="font-medium">{item.reliabilityScore}</span>
                  </span>
                  <span className="font-semibold">{formatPercent(item.probability)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {data?.riskRegisterHeatmap && data.riskRegisterHeatmap.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Risk Register → Revenue Heatmap</CardTitle>
            <CardDescription>Revenue impact of different risks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.riskRegisterHeatmap.map((item, index) => (
                <div key={index} className="flex justify-between items-center p-2 border rounded">
                  <span className="text-sm">{item.risk}</span>
                  <span className="font-semibold">{formatCurrency(item.revenueImpact)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
